/**
 * Single-flight guard for an async SSE connect path.
 *
 * WHY THIS EXISTS. `useNotifications.connectSSE` awaits twice (a Clerk token,
 * then the stream ticket) before it publishes its EventSource into
 * `eventSourceRef`. That ref survives an effect re-run, but `cancelledRef` is
 * reset to false at the top of every run, so an attempt still in flight when
 * the effect tears down resumes AFTER the reset, passes the cancelled check,
 * and overwrites the ref. The stream it overwrote becomes unreachable by every
 * close path (onerror, effect cleanup, the next connect) and stays OPEN on the
 * server until the upstream max-connection-duration recycles it, counting
 * against MAX_CONNECTIONS_PER_USER the whole time.
 *
 * The guard hands each attempt a token and lets it ask whether it has been
 * superseded, so only the newest attempt may open a connection. `cancelledRef`
 * is NOT replaced by this: it still short-circuits work after unmount. This
 * closes the narrower window where the flag has already been reset by a later
 * run but an older attempt is still resuming.
 *
 * Pure module by design (no React, no logger, no analytics), matching
 * `sseDropTracker.ts` and `sseStreamFailure.ts`: the SSE consumers read
 * `import.meta.env` transitively and cannot load under ts-jest, so the
 * testable logic lives out here.
 *
 * Deliberately NOT wired into CommentThread: its `es` and `cancelled` are
 * closure-scoped per effect run, so a superseded closure stays cancelled
 * forever and cannot resurrect. Adding the guard there would be inert code.
 */

export interface SseConnectGuard {
  /** Claim the connect slot. Returns a token to check before publishing. */
  begin(): number;
  /** True once a newer attempt has begun, or after `invalidate()`. */
  isStale(token: number): boolean;
  /** Supersede every in-flight attempt without starting one (unmount). */
  invalidate(): void;
}

export function createSseConnectGuard(): SseConnectGuard {
  // Monotonic, so a token can never be accidentally revived by a later
  // attempt landing on the same value.
  let epoch = 0;

  return {
    begin(): number {
      epoch += 1;
      return epoch;
    },
    isStale(token: number): boolean {
      return token !== epoch;
    },
    invalidate(): void {
      epoch += 1;
    },
  };
}
